import { IRosterBooking } from 'common-components/roster-control/interfaces/roster-interfaces';
import moment from 'moment-timezone';
import { FieldLabel, Text } from 'common-components/typography';
import { Avatar, Icon } from 'antd';
import { HyperlinkButton } from 'common-components/buttons';
import React, { useState } from 'react';
import { BookingTypeIcon, StatusTag } from 'common-components/tags';
import { BookingStatus, BookingType, ServiceType, ShiftSlotStatus } from 'utilities/enum-utils';
import { IRootDispatch } from 'stores/rematch/root-store';
import { useDispatch } from 'react-redux';
import CommonUtils from 'utilities/common-utils';
import PermissionUtils from 'utilities/permission-utils';
import _ from 'lodash';
import TextTag from 'common-components/tags/TextTag';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import { RecommendedPanel } from 'common-components/roster-control/common/RecommendedPanel';
import { useFetchSessionCapacity } from 'stores/hooks/query-hooks/use-query-fetch-session-capacity';
import { useFetchCustomerRatio } from 'stores/hooks/query-hooks/use-query-fetch-customer-ratio';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Box,
  Button,
  Collapse,
  Group,
  Paper,
  ScrollArea,
  ThemeIcon,
  UnstyledButton,
  Title,
  Stack,
} from '@good/ui/core';
import { Link } from 'react-router-dom';
import { usePortalUser } from 'utilities/react-hooks/use-portal-user';
import { getBookingTheme } from '../components/roster-utilities';
import { AssignWorkerBookingInline } from 'views/bookings-v2/bookings-roster-view/drawers/assign-worker-booking-inline/assign-worker-booking-inline';
import RemoveWorkerBookingInline from 'views/bookings-v2/bookings-roster-view/drawers/remove-worker-booking-inline';
import { ArrowTopRight, ChevronDown, ChevronUp } from '@good/icons';

interface BookingDrawerProps {
  booking: IRosterBooking;
  refreshAndCloseData: () => void;
  setCanClosePopover: (value: boolean) => void;
}

export const BookingDrawer = ({ booking, refreshAndCloseData, setCanClosePopover }: BookingDrawerProps) => {
  const {
    status,
    startDateTime,
    endDateTime,
    serviceName,
    attachmentUrl,
    workerAttachmentUrl,
    customerFirstName,
    customerLastName,
    workerFirstName,
    workerLastName,
    bookingId,
    serviceId,
    serviceDateTimeId,
  } = booking;
  const portalUser = usePortalUser();
  const {
    groupServiceStore: groupServiceDispatch,
    navigationStore: navigationDispatch,
    bookingsStore: bookingDispatch,
  } = useDispatch<IRootDispatch>();
  const { setCustomerBookingToOpen } = groupServiceDispatch;
  const { setSelectedSideNavMenuKeys } = navigationDispatch;
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const { doAssignWorker } = bookingDispatch;
  const { pinc1022HideStaffingOverview } = useFlags();
  const { bgColor, borderColor } = getBookingTheme(booking.status);
  const dateDisplay = moment(startDateTime).format('dddd, D MMMM YYYY');
  const timeDisplay = (
    <>
      <b>{moment(startDateTime).format('h:mm a')}</b> - {moment(endDateTime).format('h:mm a')}
    </>
  ); // const timeDisplay = '8:30 AM - 5:30 PM';
  // Get customer ratio
  let customerRatio:
    | string
    | {
        teamMemberCustomerRatio: string;
        startDateTime: string;
        endDateTime: string;
        isDuration: boolean;
        comments: string;
      }[] = '1:1';
  const { data: ratioData, isLoading: ratioIsLoading } = useFetchCustomerRatio(
    {
      bookingId,
    },
    booking.serviceType === ServiceType.GROUP,
  );
  const { data: sessionData, isLoading: sessionIsLoading } = useFetchSessionCapacity(
    {
      serviceId,
      serviceDateTimeId,
    },
    booking.serviceType === ServiceType.GROUP,
  );

  if (booking.serviceType === ServiceType.GROUP) {
    const defaultRatio = ratioData?.data?.defaultRatio;
    const ratios = ratioData?.data?.ratios;

    if (_.isEmpty(ratios)) {
      customerRatio = defaultRatio;
    } else if (ratios.length === 1) {
      customerRatio = !_.isEmpty(ratios) ? ratios[0].teamMemberCustomerRatio : defaultRatio;
    } else {
      customerRatio = ratios;
    }
  }

  const customerFullName = `${customerFirstName} ${customerLastName}`;
  const workerFullName = workerFirstName ? `${workerFirstName} ${workerLastName}` : null;

  const canAssignWorker =
    !!_.find([ShiftSlotStatus.UNASSIGNED], (status) => status === booking.shiftSlotStatus) &&
    ((booking.serviceType === ServiceType.INDIVIDUAL &&
      (booking.status === BookingStatus.PENDING ||
        booking.status === BookingStatus.ACCEPTED ||
        booking.status === BookingStatus.CONFIRMED)) ||
      booking.serviceType === ServiceType.GROUP);

  const canRemoveWorker =
    !!_.find([ShiftSlotStatus.PENDING, ShiftSlotStatus.CONFIRMED], (status) => status === booking.shiftSlotStatus) &&
    ((booking.serviceType === ServiceType.INDIVIDUAL &&
      (booking.status === BookingStatus.PENDING ||
        booking.status === BookingStatus.ACCEPTED ||
        booking.status === BookingStatus.CONFIRMED)) ||
      booking.serviceType === ServiceType.GROUP);

  const isCancelled = !!_.find(
    [
      BookingStatus.BUSINESS_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED,
      BookingStatus.CUSTOMER_CANCELLED_WITH_FEE,
      BookingStatus.CUSTOMER_CANCELLED_WITHOUT_FEE,
    ],
    (status) => status === booking.status,
  );

  const hasEditWorkerPermissions = PermissionUtils.validatePermission(
    'EditWorkerInBooking',
    portalUser.permissions.permissionRoles,
    booking.serviceDepartmentId,
    booking.serviceId,
  );

  const showBookingActions =
    hasEditWorkerPermissions &&
    !isCancelled &&
    booking.serviceType === ServiceType.INDIVIDUAL &&
    (canAssignWorker || canRemoveWorker);

  const goToBooking = async () => {
    if (booking.serviceType === ServiceType.GROUP) {
      await setCustomerBookingToOpen({ attendanceId: booking.bookingId });
      setSelectedSideNavMenuKeys(['/services']);
    } else {
      setSelectedSideNavMenuKeys(['/bookings']);
    }
  };

  const goToService = () => {
    setSelectedSideNavMenuKeys(['/services']);
  };

  const goToSession = () => {
    setSelectedSideNavMenuKeys(['/services']);
  };

  const doRefresh = async (doRefreshData = false) => {
    setCanClosePopover(true);
    doRefreshData && refreshAndCloseData();
  };

  const onSubmitAssign = async ({
    selectedWorker,
    editRecurringMode,
    shiftSlotStatus,
    isRemovePendingShiftSlots,
    ignoreShiftClashShiftIds,
  }) => {
    const data = {
      bookingId: booking.bookingId,
      bookingRequestId: booking.bookingRequestId,
      supportWorkerId: selectedWorker.supportWorkerId,
      startDateTime: booking.startDateTime,
      isRecurring: booking.isRecurring,
      shiftSlotStatus,
      isRemovePendingShiftSlots,
      editRecurringMode,
      bookingType: booking.bookingType,
      doNotSetBookingItem: true,
      ignoreShiftClashShiftIds,
    };
    return await doAssignWorker(data);
  };

  console.log({ canAssignWorker, canRemoveWorker, bookingStatus: booking.status });

  return (
    <ScrollArea h='100%'>
      <div className='flex-column line-height-100 select-none'>
        <Box pr='md'>
          <Paper
            withBorder
            p='md'
            shadow='lg'
            bg={bgColor}
            style={{
              borderColor: borderColor,
              borderLeft: 'none',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <Group gap='xs' justify='space-between' wrap='nowrap'>
              <div className='mb-x-small line-height-100 inline-block'>
                <StatusTag status={status} size='small' />
              </div>
              <Link
                to={{
                  pathname:
                    booking.serviceType === ServiceType.GROUP
                      ? `/group-service/${booking.serviceId}/session/details/${booking.serviceDateTimeId}`
                      : `/bookings/details/${booking.bookingId}`,
                  state: {
                    bookingId: booking.bookingId,
                    from: {
                      url: '/bookings?view=calendar',
                      linkLabel: 'Back to Calendar',
                    },
                  },
                }}
              >
                <Button onClick={goToBooking}>View</Button>
              </Link>
            </Group>
            <div className='line-height-120 mb-medium'>
              <Text size='x-large' lineHeight={135} weight='bold'>
                {dateDisplay}
              </Text>
              <br />
              <Text size='x-large' lineHeight={135}>
                {timeDisplay}
              </Text>
              <br />
              <Text size='x-large' lineHeight={135}>
                <Text size='x-large' color={'secondary'}>
                  {CommonUtils.formatDurationString(moment(startDateTime), moment(endDateTime))}
                </Text>
              </Text>

              <div className='align-center mt-medium flex-row'>
                {' '}
                <BookingTypeIcon
                  type={
                    booking.bookingType === BookingType.ACTIVITY_RECORD
                      ? BookingType.ACTIVITY_RECORD
                      : booking.serviceType
                  }
                  className={'mr-small'}
                />
                <Text color={'tertiary'}>{CommonUtils.formatServiceTypeStatus(booking)}</Text>
              </div>

              {/* Display service information in logical order for Group Services */}
              {booking.serviceType === ServiceType.GROUP ? (
                <div className='mt-small'>
                  {/* Service name */}
                  <div className='mb-x-small'>
                    <Link to={`/group-service/details/${booking.serviceId}`}>
                      <HyperlinkButton color={'black'} onClick={goToService}>
                        {serviceName}
                      </HyperlinkButton>
                    </Link>
                  </div>

                  {/* Schedule name */}
                  {booking.scheduleName && (
                    <div className='mb-x-small'>
                      <Text size='large' color='secondary'>
                        <Text size='medium' color='tertiary'>
                          Schedule:{' '}
                        </Text>
                        <Text color='primary'>{booking.scheduleName}</Text>
                      </Text>
                    </div>
                  )}

                  {/* Session name (description) */}
                  {booking.description && (
                    <div className='mb-x-small'>
                      <Text size='large' color='secondary'>
                        <Text size='medium' color='tertiary'>
                          Session:{' '}
                        </Text>
                        <Text weight='600' color='primary'>
                          {booking.description}
                        </Text>
                      </Text>
                    </div>
                  )}

                  <div className='mt-small'>
                    <Link to={`/group-service/${booking.serviceId}/session/details/${booking.serviceDateTimeId}`}>
                      <HyperlinkButton onClick={goToSession}>Go to session</HyperlinkButton>
                    </Link>
                  </div>
                </div>
              ) : (
                /* Individual service display */
                <Link to={`/service/details/${booking.serviceId}`}>
                  <HyperlinkButton color={'black'} onClick={goToService}>
                    {serviceName}
                  </HyperlinkButton>
                </Link>
              )}
            </div>
            {booking.serviceType === ServiceType.GROUP && !pinc1022HideStaffingOverview && (
              <div className={'mb-large'}>
                <div className='mt-x-small'>
                  {sessionIsLoading ? (
                    <SpinningLoader size={20} message={'Fetching recommended capacity...'} />
                  ) : (
                    sessionData?.data && (
                      <RecommendedPanel
                        recommendedTotalShiftSlot={sessionData.data.recommendedTotalShiftSlot}
                        totalAssignedShiftSlot={sessionData.data.totalAssignedShiftSlot}
                      />
                    )
                  )}
                </div>
              </div>
            )}

            <div className='mb-large'>
              <FieldLabel text={'CUSTOMER'} />
              <div className='align-center mt-x-small flex-row'>
                <Avatar icon='user' className='mr-small' src={attachmentUrl} />
                <Text>{customerFullName}</Text>
              </div>
            </div>

            {booking.serviceType === ServiceType.GROUP && (
              <div className='mb-large'>
                <FieldLabel text={'Customer ratio'} />
                <div className='mt-x-small'>
                  {ratioIsLoading ? (
                    <SpinningLoader size={20} message={'Fetching ratios...'} />
                  ) : _.isString(customerRatio) ? (
                    <TextTag
                      content={customerRatio}
                      className={'mr-x-small'}
                      size={'regular'}
                      theme={'light'}
                      color={'blue'}
                    />
                  ) : (
                    customerRatio.map((ratio, index) => {
                      return (
                        <>
                          <div className={`mt-x-small flex-row ${index !== 0 && 'mt-small'} `}>
                            <div className={'mr-small'}>
                              <TextTag
                                content={ratio.teamMemberCustomerRatio}
                                size={'small'}
                                theme={'light'}
                                color={'blue'}
                                className={'inline-block'}
                              />
                            </div>
                            <div>
                              <div style={{ marginTop: '-1px' }}>
                                {ratio.isDuration ? (
                                  <Text size='large'>
                                    {' '}
                                    {CommonUtils.formatDurationString(
                                      moment(ratio.startDateTime),
                                      moment(ratio.endDateTime),
                                    )}
                                  </Text>
                                ) : (
                                  <Text size='large'>
                                    {moment.tz(ratio.startDateTime, booking.timezone).format('h:mm a')}
                                    {' - '}
                                    {moment.tz(ratio.endDateTime, booking.timezone).format('h:mm a')}
                                  </Text>
                                )}
                              </div>
                              {ratio.comments && (
                                <div>
                                  <Text color={'secondary'} size={'regular'}>
                                    {ratio.comments}
                                  </Text>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {booking.serviceType === ServiceType.GROUP &&
              booking.shiftCustomerAssignments &&
              booking.shiftCustomerAssignments.length > 0 && (
                <div className='mb-large'>
                  <FieldLabel text={'TEAM MEMBER'} />
                  <div className='mt-x-small' style={{ overflow: 'auto', maxHeight: '120px' }}>
                    {_.map(booking.shiftCustomerAssignments, (assignment) => {
                      return (
                        <div className='align-center mt-x-small flex-row'>
                          <div className='line-height-120 mb-small flex-row'>
                            <Avatar
                              icon='user'
                              size='small'
                              className='mr-small'
                              src={assignment.customerAttachmentUrl}
                            />
                            <Text size='regular'>
                              <span className={'text-weight-bold'}>
                                {assignment.supportWorkerFirstName} {assignment.supportWorkerLastName}
                              </span>
                              <br />
                              <span>
                                {moment.tz(assignment.startDateTime, booking.timezone).format('h:mm a')}{' '}
                                {moment.tz(assignment.endDateTime, booking.timezone).format('h:mm a')}
                              </span>
                            </Text>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            {booking.serviceType === ServiceType.INDIVIDUAL && (
              <div className='mb-large'>
                <FieldLabel text={'TEAM MEMBER'} />
                <div className='align-center mt-x-small flex-row'>
                  {workerFullName ? (
                    <>
                      <Avatar icon='user' className='mr-small' shape='square' src={workerAttachmentUrl} />
                      <Text>{workerFullName}</Text>
                    </>
                  ) : booking.shiftSlotStatus === ShiftSlotStatus.PUBLISHING ? (
                    <Text>
                      <Icon type='wifi' className='text-color-violet-light mr-x-small' />
                      Published
                    </Text>
                  ) : (
                    <Text color='secondary'>No team member assigned</Text>
                  )}
                </div>
              </div>
            )}

            <div className={'mb-large'}>
              <FieldLabel text={'TOTAL'} />
              <div className='align-center mt-x-small flex-row'>
                <Text>{CommonUtils.formatPrice(Number(booking.total))}</Text>
              </div>
            </div>
          </Paper>
        </Box>
      </div>

      <Box p='md'>
        {showBookingActions && (
          <>
            {canAssignWorker && booking.address && (
              <AssignWorkerBookingInline
                onReset={doRefresh}
                serviceId={booking.serviceId}
                bookingId={booking.bookingId}
                bookingRequestId={booking.bookingRequestId}
                onSubmitAssign={onSubmitAssign}
                isRecurring={booking.isRecurring}
                address={booking.address}
                sessions={[]}
                isEditingBooking={true}
                startDateTime={booking.startDateTime}
                endDateTime={booking.endDateTime}
                customerUserIds={[booking.bookerUserId]}
                displayTimezone={booking.timezone}
                bookingType={booking.bookingType}
              />
            )}
            {canRemoveWorker && booking.status && (
              <div>
                <UnstyledButton w='100%' onClick={() => setFiltersCollapsed(!filtersCollapsed)}>
                  <Group justify='space-between'>
                    <Group gap={6}>
                      <Title order={4}>Remove team member from booking</Title>
                      <ThemeIcon variant='transparent' size='sm'>
                        {filtersCollapsed ? <ChevronDown /> : <ChevronUp />}
                      </ThemeIcon>
                    </Group>
                  </Group>
                </UnstyledButton>
                <Collapse in={!filtersCollapsed}>
                  <RemoveWorkerBookingInline
                    bookingType={booking.bookingType}
                    onRemoved={doRefresh}
                    selectedBooking={booking}
                  />
                </Collapse>
              </div>
            )}
          </>
        )}
        {booking.serviceType === ServiceType.GROUP && (
          <Stack gap='xs' mt='xs'>
            <Title order={4} lh={1.25}>
              To manage, please go to the session details page.
            </Title>
            <Link to={`/group-service/${booking.serviceId}/session/details/${booking.serviceDateTimeId}`}>
              <Button variant='default' onClick={goToSession} rightSection={<ArrowTopRight />}>
                Go to session
              </Button>
            </Link>
          </Stack>
        )}
      </Box>
    </ScrollArea>
  );
};
